import { create } from 'zustand';
import { fetchIntroVersion, setIntroVersion } from '../lib/supabase';

/**
 * Begrüßung und Rundgänge — wie im Event Manager.
 *
 * Gemerkt wird eine Fassungsnummer, kein Haken: Wird die Einführung später
 * überarbeitet, zeigt eine höhere Fassung sie allen erneut — ohne dass
 * jemand bei jeder Person etwas zurücksetzen muss.
 */
export const TOUR_VERSION = 1;

interface EinfuehrungStore {
  geseheneVersion: number | null;
  geladen: boolean;
  begruessung: boolean;
  /** Laufende Tour, null = keine. */
  tourId: string | null;
  schritt: number;
  /** Tour ist gewünscht, der Seitenwechsel dorthin läuft noch. */
  angefordert: string | null;

  laden: () => Promise<void>;
  faellig: () => boolean;
  begruessungZeigen: () => void;
  spaeter: () => void;
  tourAnfordern: (id: string) => void;
  tourStarten: (id: string) => void;
  springen: (i: number) => void;
  weiter: (anzahl: number) => void;
  zurueck: () => void;
  beenden: () => void;
}

function versionMerken() {
  setIntroVersion(TOUR_VERSION).catch((e) =>
    console.warn('Einführungs-Merker nicht speicherbar:', (e as Error).message),
  );
}

export const useEinfuehrungStore = create<EinfuehrungStore>((set, get) => ({
  geseheneVersion: null,
  geladen: false,
  begruessung: false,
  tourId: null,
  schritt: 0,
  angefordert: null,

  laden: async () => {
    // Erst zurücksetzen: Am geteilten Rechner darf niemand den Stand des
    // vorherigen Kontos erben.
    set({ geseheneVersion: null, geladen: false, begruessung: false, tourId: null, schritt: 0 });
    try {
      const v = await fetchIntroVersion();
      set({ geseheneVersion: v, geladen: true });
    } catch (e) {
      // Lässt sich der Merker nicht lesen, bleibt der automatische Start aus —
      // niemand soll bei jeder Anmeldung angesprungen werden. Der Knopf
      // „Einführung" in der Kopfzeile funktioniert trotzdem.
      console.warn('Einführung startet nicht von selbst:', (e as Error).message);
      set({ geseheneVersion: TOUR_VERSION, geladen: true });
    }
  },

  faellig: () => {
    const s = get();
    return s.geladen && !s.begruessung && !s.tourId && (s.geseheneVersion ?? 0) < TOUR_VERSION;
  },

  begruessungZeigen: () => set({ begruessung: true, tourId: null }),

  spaeter: () => {
    set({ begruessung: false, geseheneVersion: TOUR_VERSION });
    versionMerken();
  },

  tourAnfordern: (id) => set({ begruessung: false, angefordert: id }),

  tourStarten: (id) => set({ begruessung: false, angefordert: null, tourId: id, schritt: 0 }),

  springen: (i) => set({ schritt: Math.max(0, i) }),

  weiter: (anzahl) => {
    const { schritt } = get();
    if (schritt + 1 >= anzahl) {
      set({ tourId: null, schritt: 0, geseheneVersion: TOUR_VERSION });
      versionMerken();
      return;
    }
    set({ schritt: schritt + 1 });
  },

  zurueck: () => set({ schritt: Math.max(0, get().schritt - 1) }),

  beenden: () => {
    set({ tourId: null, schritt: 0, begruessung: false, geseheneVersion: TOUR_VERSION });
    versionMerken();
  },
}));
